import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import DashboardTable from '../../components/DashboardTable';
import Alert from '@mui/joy/Alert';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import DialogActions from '@mui/joy/DialogActions';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import TableCustomerCell from '../../components/TableCustomerCell';
import SaleStatusChip from '../../components/SaleStatusChip';
import { Sale } from '../../types/sale';
import { salesApi, SalesResponse, SalesSearchParams } from '../../api/sales.api';
import { clientsApi } from '../../api/clients.api';
import { Client } from '../../types/client';
import { debounce } from 'lodash';
import { formatRecordReference } from '../../utils/formatRecordReference';

const SaleList: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  
  // Filtros
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState<Client[]>([]);
  const [showClientResults, setShowClientResults] = useState(false);
  const [selectedClientReference, setSelectedClientReference] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [datePanelOpen, setDatePanelOpen] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState<string>('');
  const [draftEndDate, setDraftEndDate] = useState<string>('');
  const clientSearchRef = useRef<HTMLDivElement>(null);
  const dateFilterRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const [saleToDelete, setSaleToDelete] = useState<Sale | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleConfirmDelete = async () => {
    if (!saleToDelete) return;
    try {
      setIsDeleting(true);
      setDeleteError(null);
      await salesApi.delete(saleToDelete.reference);
      setSaleToDelete(null);
      const filters: Partial<SalesSearchParams> = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (selectedClientReference) filters.clientReference = selectedClientReference;
      if (selectedStatus) filters.status = selectedStatus;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      await loadSales(currentPage, itemsPerPage, filters);
    } catch (error) {
      setDeleteError(
        error instanceof Error ? error.message : 'No se pudo eliminar la venta',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const statusTabs: { value: string; label: string }[] = [
    { value: '', label: 'Ver todo' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'paid_pending', label: 'Pagada (entrega)' },
    { value: 'completed', label: 'Completada' },
    { value: 'cancelled', label: 'Cancelada' },
  ];

  // Cerrar resultados de búsqueda al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (clientSearchRef.current && !clientSearchRef.current.contains(target)) {
        setShowClientResults(false);
      }
      if (dateFilterRef.current && !dateFilterRef.current.contains(target)) {
        setDatePanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const filters: Partial<SalesSearchParams> = {
      page: currentPage,
      limit: itemsPerPage,
    };
    if (selectedClientReference) {
      filters.clientReference = selectedClientReference;
    }
    if (selectedStatus) {
      filters.status = selectedStatus;
    }
    if (startDate) {
      filters.startDate = startDate;
    }
    if (endDate) {
      filters.endDate = endDate;
    }
    loadSales(currentPage, itemsPerPage, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    itemsPerPage,
    selectedStatus,
    selectedClientReference,
    startDate,
    endDate,
  ]);

  // Búsqueda de clientes con debounce
  const debouncedClientSearch = debounce(async (term: string) => {
    try {
      const results = await clientsApi.search(term);
      setClientSearchResults(results);
      setShowClientResults(true);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientSearchResults([]);
    }
  }, 300);

  const handleClientSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setClientSearchTerm(term);
    setSelectedClientReference(undefined);

    debouncedClientSearch(term);
  };

  const handleClientFocus = () => {
    // Cargar los últimos 10 clientes al hacer foco en el campo
    debouncedClientSearch('');
  };

  const handleSelectClient = (client: Client) => {
    setSelectedClientReference(client.reference);
    setClientSearchTerm(client.name);
    setShowClientResults(false);
  };

  const loadSales = async (page: number = 1, limit: number = 10, filters?: Partial<SalesSearchParams>) => {
    try {
      setLoading(true);
      setError(null);
      const response = await salesApi.getAll(page, limit, filters);
      
      // Verificar que la respuesta tenga la estructura esperada
      if (response && response.data) {
        setSales(response.data);
        updatePagination(response);
      } else {
        console.error('Respuesta inesperada:', response);
        setError('Error al cargar los datos. La respuesta no tiene el formato esperado.');
        setSales([]);
      }
    } catch (error) {
      console.error('Error loading sales:', error);
      setError('Error al cargar los datos. Por favor, intente de nuevo más tarde.');
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setClientSearchTerm('');
    setSelectedClientReference(undefined);
    setSelectedStatus('');
    setStartDate('');
    setEndDate('');
    setDraftStartDate('');
    setDraftEndDate('');
    setDatePanelOpen(false);
    setCurrentPage(1);
  };

  const openDatePanel = () => {
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setDatePanelOpen((open) => !open);
  };

  const applyDateRange = () => {
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setDatePanelOpen(false);
    setCurrentPage(1);
  };

  const clearDateRange = () => {
    setDraftStartDate('');
    setDraftEndDate('');
    setStartDate('');
    setEndDate('');
    setDatePanelOpen(false);
    setCurrentPage(1);
  };

  const dateButtonLabel = (): string => {
    if (startDate && endDate) {
      return `${startDate} → ${endDate}`;
    }
    if (startDate) {
      return `Desde ${startDate}`;
    }
    if (endDate) {
      return `Hasta ${endDate}`;
    }
    return 'Fecha de venta';
  };

  const updatePagination = (response: SalesResponse) => {
    if (response) {
      setCurrentPage(response.page || 1);
      setTotalPages(response.lastPage || 1);
      setTotalItems(response.total || 0);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatTotal = (total: number | string) => {
    const numTotal = Number(total);
    return isNaN(numTotal) ? '0.00' : numTotal.toFixed(2);
  };

  // Generar el array de páginas para la paginación
  const paginationItems: React.ReactNode[] = [];
  const paginationEllipsis = (key: string) => (
    <Typography key={key} level="body-sm" sx={{ px: 1.5, py: 1 }}>
      ...
    </Typography>
  );

  paginationItems.push(
    <Button
      key={1}
      size="sm"
      variant={currentPage === 1 ? 'solid' : 'outlined'}
      color={currentPage === 1 ? 'primary' : 'neutral'}
      onClick={() => handlePageChange(1)}
    >
      1
    </Button>,
  );
  
  // Lógica para mostrar ellipsis y páginas cercanas a la actual
  if (totalPages > 1) {
    // Si hay muchas páginas, mostrar ellipsis antes y después
    if (totalPages > 7) {
      // Mostrar ellipsis al inicio si estamos lejos de la página 1
      if (currentPage > 4) {
        paginationItems.push(paginationEllipsis('e1'));
      }
      
      // Mostrar páginas cercanas a la actual
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        paginationItems.push(
          <Button
            key={i}
            size="sm"
            variant={currentPage === i ? 'solid' : 'outlined'}
            color={currentPage === i ? 'primary' : 'neutral'}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Button>,
        );
      }
      
      // Mostrar ellipsis al final si estamos lejos de la última página
      if (currentPage < totalPages - 3) {
        paginationItems.push(paginationEllipsis('e2'));
      }
    } else {
      // Si hay pocas páginas, mostrar todas
      for (let i = 2; i < totalPages; i++) {
        paginationItems.push(
          <Button
            key={i}
            size="sm"
            variant={currentPage === i ? 'solid' : 'outlined'}
            color={currentPage === i ? 'primary' : 'neutral'}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Button>,
        );
      }
    }
    
    // Siempre mostrar última página si hay más de una página
    paginationItems.push(
      <Button
        key={totalPages}
        size="sm"
        variant={currentPage === totalPages ? 'solid' : 'outlined'}
        color={currentPage === totalPages ? 'primary' : 'neutral'}
        onClick={() => handlePageChange(totalPages)}
      >
        {totalPages}
      </Button>,
    );
  }

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader
        title="Ventas"
        action={
          <Button component={RouterLink} to="/sales/new" color="primary">
            Nueva venta
          </Button>
        }
      />

      <Sheet
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 'md',
          boxShadow: 'sm',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            flexWrap="wrap"
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.5,
                p: 0.5,
                overflowX: 'auto',
                maxWidth: '100%',
                borderRadius: 'lg',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.level1',
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              {statusTabs.map((tab) => {
                const isActive = selectedStatus === tab.value;
                return (
                  <Button
                    key={tab.value === '' ? 'all' : tab.value}
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => {
                      setSelectedStatus(tab.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      flexShrink: 0,
                      borderRadius: 'md',
                      px: 1.25,
                      fontWeight: isActive ? 'lg' : 'md',
                      fontSize: 'sm',
                      bgcolor: isActive ? 'background.surface' : 'transparent',
                      color: isActive ? 'text.primary' : 'neutral.600',
                      boxShadow: isActive ? 'sm' : 'none',
                      border: isActive ? '1px solid' : '1px solid transparent',
                      borderColor: isActive ? 'divider' : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? 'background.surface' : 'neutral.softHoverBg',
                      },
                    }}
                  >
                    {tab.label}
                  </Button>
                );
              })}
            </Box>
            <Button size="sm" variant="plain" color="neutral" onClick={handleClearFilters}>
              Restablecer
            </Button>
          </Stack>
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            useFlexGap
            sx={{ gap: 1 }}
          >
            <Box
              ref={clientSearchRef}
              sx={{
                position: 'relative',
                flex: '1 1 200px',
                minWidth: 160,
              }}
            >
              <Input
                size="sm"
                id="client"
                value={clientSearchTerm}
                onChange={handleClientSearchChange}
                onFocus={handleClientFocus}
                placeholder="Buscar por cliente"
                aria-label="Buscar cliente"
                startDecorator={<SearchRoundedIcon sx={{ fontSize: 18 }} />}
              />
              {showClientResults && clientSearchResults.length > 0 && (
                <Sheet
                  variant="outlined"
                  sx={{
                    position: 'absolute',
                    zIndex: 10,
                    mt: 0.5,
                    width: '100%',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {clientSearchResults.map((client) => (
                    <Box
                      key={client.reference}
                      onClick={() => {
                        handleSelectClient(client);
                        setCurrentPage(1);
                      }}
                      sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'background.level1' } }}
                    >
                      <Typography level="body-sm" sx={{ fontWeight: 'md' }}>
                        {client.name}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {client.phoneNumber}
                      </Typography>
                    </Box>
                  ))}
                </Sheet>
              )}
              {showClientResults && clientSearchResults.length === 0 && clientSearchTerm.trim().length >= 2 && (
                <Sheet variant="outlined" sx={{ position: 'absolute', zIndex: 10, mt: 0.5, width: '100%', p: 1 }}>
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    No se encontraron clientes
                  </Typography>
                </Sheet>
              )}
            </Box>
            <Box ref={dateFilterRef} sx={{ position: 'relative', flexShrink: 0 }}>
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<CalendarMonthOutlinedIcon />}
                onClick={openDatePanel}
                sx={{
                  borderColor: 'divider',
                  maxWidth: { xs: '100%', sm: 280 },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {dateButtonLabel()}
              </Button>
              {datePanelOpen ? (
                <Sheet
                  variant="outlined"
                  sx={{
                    position: 'absolute',
                    zIndex: 20,
                    right: 0,
                    mt: 0.5,
                    p: 1.5,
                    minWidth: 280,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    boxShadow: 'md',
                  }}
                >
                  <Typography level="title-sm">Rango de fechas</Typography>
                  <Input
                    size="sm"
                    type="date"
                    value={draftStartDate}
                    onChange={(e) => setDraftStartDate(e.target.value)}
                    slotProps={{ input: { 'aria-label': 'Fecha desde' } }}
                  />
                  <Input
                    size="sm"
                    type="date"
                    value={draftEndDate}
                    onChange={(e) => setDraftEndDate(e.target.value)}
                    slotProps={{ input: { 'aria-label': 'Fecha hasta' } }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                    <Button size="sm" variant="plain" color="neutral" onClick={clearDateRange}>
                      Quitar fechas
                    </Button>
                    <Button size="sm" color="primary" onClick={applyDateRange}>
                      Aplicar
                    </Button>
                  </Stack>
                </Sheet>
              ) : null}
            </Box>
          </Stack>
        </Stack>
      </Sheet>

      {loading && (
        <Typography level="body-sm" sx={{ textAlign: 'center', py: 2 }}>
          Buscando ventas...
        </Typography>
      )}

      {error && <Alert color="danger">{error}</Alert>}

      {!loading && !error && sales && sales.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          No hay ventas registradas
        </Typography>
      ) : (
        !loading &&
        !error &&
        sales && (
          <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
            <DashboardTable stickyHeader>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.reference}>
                    <td>#{formatRecordReference(sale.reference)}</td>
                    <td>
                      <TableCustomerCell
                        name={sale.client?.name || 'Cliente no encontrado'}
                      />
                    </td>
                    <td>{new Date(sale.date).toLocaleDateString()}</td>
                    <td>${formatTotal(sale.total)}</td>
                    <td>
                      <SaleStatusChip status={sale.status} />
                    </td>
                    <td>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Ver detalle" variant="soft" size="sm">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => navigate(`/sales/${sale.reference}`)}
                            aria-label="Ver detalle"
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar" variant="soft" size="sm">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="primary"
                            onClick={() => navigate(`/sales/edit/${sale.reference}`)}
                            aria-label="Editar"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar" variant="soft" size="sm">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => {
                              setDeleteError(null);
                              setSaleToDelete(sale);
                            }}
                            aria-label="Eliminar"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DashboardTable>

            <Box
              sx={{
                ...LAYOUT.tableFooterPadding,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: LAYOUT.stackTight,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography level="body-sm">
                  Mostrando {sales.length} de {totalItems} resultados
                </Typography>
                <Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={(_, value) => {
                    if (value != null) {
                      setItemsPerPage(Number(value));
                      setCurrentPage(1);
                    }
                  }}
                >
                  <Option value={10}>10 por página</Option>
                  <Option value={25}>25 por página</Option>
                  <Option value={50}>50 por página</Option>
                  <Option value={100}>100 por página</Option>
                </Select>
              </Stack>

              {totalPages > 1 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                  <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    startDecorator={<ChevronLeftRoundedIcon />}
                  >
                    Anterior
                  </Button>
                  {paginationItems}
                  <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    endDecorator={<ChevronRightRoundedIcon />}
                  >
                    Siguiente
                  </Button>
                </Stack>
              )}
            </Box>
          </Sheet>
        )
      )}
      <Modal
        open={Boolean(saleToDelete)}
        onClose={() => {
          if (!isDeleting) setSaleToDelete(null);
        }}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRoundedIcon color="error" />
            Eliminar venta
          </DialogTitle>
          <DialogContent>
            ¿Seguro que querés eliminar la venta
            {saleToDelete ? ` #${formatRecordReference(saleToDelete.reference)}` : ''}? Esta
            acción no se puede deshacer.
            {deleteError && (
              <Alert color="danger" variant="soft" sx={{ mt: 1.5 }}>
                {deleteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              loading={isDeleting}
              onClick={handleConfirmDelete}
            >
              Eliminar
            </Button>
            <Button
              variant="plain"
              color="neutral"
              disabled={isDeleting}
              onClick={() => setSaleToDelete(null)}
            >
              Cancelar
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </PageStack>
  );
};

export default SaleList;